import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import SuperAdminLayout from '../SuperAdminLayout';
import { saRequest } from '../../../utils/superAdminAxiosCaller';
import { getSuperAdminToken } from '../../../utils/superAdminToken';
import { apiUrl } from '../../../utils/apiCaller';
import AuditLogDetailModal from './AuditLogDetailModal';
import { describeAction, severityFor, Severity } from './describeAction';

interface AuditRow {
    id: number;
    occurred_at: string | null;
    action: string;
    actor_email: string | null;
    actor_name: string | null;
    super_admin_id: number | null;
    target_type: string | null;
    target_id: number | null;
    target_company_id: number | null;
    target_company_name: string | null;
    target_user_email: string | null;
    target_user_username: string | null;
    target_user_fullname: string | null;
    impersonation_session_id: number | null;
    ip: string | null;
    user_agent: string | null;
    metadata: Record<string, any> | null;
}

interface IndexResponse {
    data: AuditRow[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    };
}

// Curated list — the most common needles. Free-text filter covers the rest.
const ACTION_PRESETS: Array<{ value: string; label: string }> = [
    { value: '', label: 'All actions' },
    { value: 'sa.login.*', label: 'Logins' },
    { value: 'sa.company.*', label: 'Company lifecycle' },
    { value: 'sa.impersonation.*', label: 'Impersonation' },
    { value: 'sa.impersonation.write_blocked', label: 'Impersonation writes blocked' },
    { value: 'sa.totp.*', label: 'TOTP events' },
    { value: 'sa.audit.*', label: 'Audit viewer activity' },
];

function formatWhen(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
}

function relativeTime(iso: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diff < 60) return diff <= 2 ? 'just now' : `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const SEVERITY_STYLES: Record<Severity, { bg: string; color: string }> = {
    danger: { bg: 'rgba(251, 113, 133, 0.14)', color: 'var(--sa-danger)' },
    notice: { bg: 'rgba(245, 158, 11, 0.14)', color: 'var(--sa-accent)' },
    info:   { bg: 'rgba(255, 255, 255, 0.06)', color: 'var(--sa-text-tertiary)' },
};

function SeverityDot({ action }: { action: string }) {
    const s = severityFor(action);
    const style = SEVERITY_STYLES[s];
    return (
        <span
            style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: style.color,
                flex: '0 0 auto',
            }}
            title={s}
            aria-label={`Severity: ${s}`}
        />
    );
}

const AuditLogPage: React.FC = () => {
    const history = useHistory();
    const [currentEmail, setCurrentEmail] = useState<string | null>(null);

    const [action, setAction] = useState<string>('');
    const [q, setQ] = useState('');
    const [fromDate, setFromDate] = useState<Date | null>(null);
    const [toDate, setToDate] = useState<Date | null>(null);
    const [companyId, setCompanyId] = useState('');

    const [rows, setRows] = useState<AuditRow[]>([]);
    const [meta, setMeta] = useState<IndexResponse['meta'] | null>(null);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [detailId, setDetailId] = useState<number | null>(null);

    useEffect(() => {
        saRequest('GET', 'auth/me')
            .then(res => setCurrentEmail(res.data.super_admin?.email ?? null))
            .catch(() => {});
    }, []);

    const toYmd = (d: Date | null): string => {
        if (!d) return '';
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    const fetchRows = useCallback(async (nextPage: number) => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            if (action) params.append('action', action);
            if (q.trim()) params.append('q', q.trim());
            if (fromDate) params.append('from_date', toYmd(fromDate));
            if (toDate) params.append('to_date', toYmd(toDate));
            if (companyId.trim()) params.append('target_company_id', companyId.trim());
            params.append('page', String(nextPage));
            params.append('per_page', '25');

            const res = await saRequest<IndexResponse>('GET', `audit-log?${params.toString()}`);
            setRows(res.data.data);
            setMeta(res.data.meta);
        } catch (err: any) {
            if (err?.response?.status === 401) {
                history.replace('/mika/ops/login');
                return;
            }
            setError(err?.response?.data?.message || 'Failed to load audit log.');
        } finally {
            setLoading(false);
        }
    }, [action, q, fromDate, toDate, companyId, history]);

    useEffect(() => {
        fetchRows(page);
    }, [fetchRows, page]);

    const applyFilters = (e: React.FormEvent) => {
        e.preventDefault();
        setPage(1);
        fetchRows(1);
    };

    const clearFilters = () => {
        setAction('');
        setQ('');
        setFromDate(null);
        setToDate(null);
        setCompanyId('');
        setPage(1);
    };

    const exportCsv = () => {
        // CSV endpoint wants the same filters but returns a file — trigger a
        // regular-browser download by synthesizing a link. We pass the bearer
        // via a query param-free fetch() + blob so the Authorization header
        // survives. That avoids the need for a separate cookie auth path.
        const token = getSuperAdminToken();
        if (!token) {
            history.replace('/mika/ops/login');
            return;
        }
        const params = new URLSearchParams();
        if (action) params.append('action', action);
        if (q.trim()) params.append('q', q.trim());
        if (fromDate) params.append('from_date', toYmd(fromDate));
        if (toDate) params.append('to_date', toYmd(toDate));
        if (companyId.trim()) params.append('target_company_id', companyId.trim());
        const url = apiUrl + 'super-admin/audit-log/export.csv' + (params.toString() ? `?${params}` : '');

        fetch(url, { headers: { Authorization: `Bearer ${token}`, Accept: 'text/csv' } })
            .then(r => {
                if (!r.ok) throw new Error('Export failed');
                return r.blob();
            })
            .then(blob => {
                const href = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = href;
                a.download = `mika-ops-audit-${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(href);
            })
            .catch(() => {
                setError('CSV export failed. Try again or narrow the filters.');
            });
    };

    return (
        <SuperAdminLayout currentEmail={currentEmail}>
            <div className="sa-page-header">
                <div>
                    <h1>Audit log</h1>
                    <div style={{ color: 'var(--sa-text-tertiary)', fontSize: 'var(--sa-fs-sm)', marginTop: 4 }}>
                        Every god-user action, append-only. {meta && <>{meta.total.toLocaleString()} total rows.</>}
                    </div>
                </div>
                <button type="button" className="sa-btn sa-btn-secondary" onClick={exportCsv} disabled={loading}>
                    Export CSV
                </button>
            </div>

            <div className="sa-card" style={{ marginBottom: 'var(--sa-s4)' }}>
                <form
                    onSubmit={applyFilters}
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                        gap: 'var(--sa-s3)',
                        alignItems: 'end',
                    }}
                >
                    <div className="sa-field" style={{ marginBottom: 0 }}>
                        <label>Action</label>
                        <select value={action} onChange={e => setAction(e.target.value)}>
                            {ACTION_PRESETS.map(p => (
                                <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="sa-field" style={{ marginBottom: 0 }}>
                        <label>Search</label>
                        <input
                            type="search"
                            placeholder="action, email, company name…"
                            value={q}
                            onChange={e => setQ(e.target.value)}
                        />
                    </div>
                    <div className="sa-field" style={{ marginBottom: 0 }}>
                        <label>Company ID</label>
                        <input
                            type="number"
                            placeholder="e.g. 5"
                            value={companyId}
                            onChange={e => setCompanyId(e.target.value)}
                        />
                    </div>
                    <div className="sa-field sa-datepicker-field" style={{ marginBottom: 0 }}>
                        <label>From</label>
                        <DatePicker
                            selected={fromDate}
                            onChange={(d: Date | null) => setFromDate(d)}
                            selectsStart
                            startDate={fromDate}
                            endDate={toDate}
                            maxDate={toDate ?? new Date()}
                            dateFormat="MMM d, yyyy"
                            placeholderText="Pick a start date"
                            isClearable
                            popperPlacement="bottom-start"
                            showPopperArrow={false}
                        />
                    </div>
                    <div className="sa-field sa-datepicker-field" style={{ marginBottom: 0 }}>
                        <label>To</label>
                        <DatePicker
                            selected={toDate}
                            onChange={(d: Date | null) => setToDate(d)}
                            selectsEnd
                            startDate={fromDate}
                            endDate={toDate}
                            minDate={fromDate ?? undefined}
                            maxDate={new Date()}
                            dateFormat="MMM d, yyyy"
                            placeholderText="Pick an end date"
                            isClearable
                            popperPlacement="bottom-start"
                            showPopperArrow={false}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--sa-s2)' }}>
                        <button type="submit" className="sa-btn sa-btn-primary" disabled={loading}>
                            {loading && <span className="sa-btn-spinner" />}
                            Apply
                        </button>
                        <button type="button" className="sa-btn sa-btn-ghost" onClick={clearFilters}>
                            Clear
                        </button>
                    </div>
                </form>
            </div>

            {error && <div className="sa-error-banner">{error}</div>}

            <div className="sa-card" style={{ padding: 0 }}>
                {loading ? (
                    <table className="sa-table">
                        <thead>
                            <tr>
                                <th style={{ width: 20 }} />
                                <th>Event</th>
                                <th style={{ width: 140 }}>When</th>
                                <th style={{ width: 110 }}>IP</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[0, 1, 2, 3, 4].map(i => (
                                <tr key={i}><td colSpan={4} style={{ padding: 0 }}><div className="sa-skeleton-row" /></td></tr>
                            ))}
                        </tbody>
                    </table>
                ) : rows.length === 0 ? (
                    <div className="sa-empty">
                        <div className="sa-empty-title">No matching audit rows</div>
                        <div className="sa-empty-hint">Try widening the date range or clearing filters.</div>
                    </div>
                ) : (
                    <table className="sa-table">
                        <thead>
                            <tr>
                                <th style={{ width: 20 }} />
                                <th>Event</th>
                                <th style={{ width: 140 }}>When</th>
                                <th style={{ width: 110 }}>IP</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr
                                    key={r.id}
                                    onClick={() => setDetailId(r.id)}
                                    style={{ cursor: 'pointer' }}
                                    title="Click for detail"
                                >
                                    <td style={{ paddingLeft: 16, paddingRight: 0, verticalAlign: 'middle' }}>
                                        <SeverityDot action={r.action} />
                                    </td>
                                    <td>
                                        <div style={{ fontSize: 'var(--sa-fs-sm)', color: 'var(--sa-text)', lineHeight: 1.4 }}>
                                            {describeAction(r)}
                                        </div>
                                        <div className="sa-mono" style={{ color: 'var(--sa-text-tertiary)', fontSize: 10, marginTop: 2, letterSpacing: '0.02em' }}>
                                            {r.action}
                                            {r.impersonation_session_id !== null && (
                                                <> · session #{r.impersonation_session_id}</>
                                            )}
                                        </div>
                                    </td>
                                    <td style={{ fontSize: 'var(--sa-fs-xs)', color: 'var(--sa-text-tertiary)' }}>
                                        <div style={{ color: 'var(--sa-text-secondary)' }}>{relativeTime(r.occurred_at)}</div>
                                        <div className="sa-mono" style={{ fontSize: 10, marginTop: 2 }}>
                                            {formatWhen(r.occurred_at)}
                                        </div>
                                    </td>
                                    <td className="sa-mono" style={{ color: 'var(--sa-text-tertiary)', fontSize: 'var(--sa-fs-xs)' }}>
                                        {r.ip ?? '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {meta && meta.last_page > 1 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'var(--sa-s4)' }}>
                    <div style={{ color: 'var(--sa-text-tertiary)', fontSize: 'var(--sa-fs-sm)' }}>
                        Page {meta.current_page} of {meta.last_page}
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--sa-s2)' }}>
                        <button
                            type="button"
                            className="sa-btn sa-btn-secondary sa-btn-sm"
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={loading || page <= 1}
                        >
                            ← Prev
                        </button>
                        <button
                            type="button"
                            className="sa-btn sa-btn-secondary sa-btn-sm"
                            onClick={() => setPage(p => Math.min(meta.last_page, p + 1))}
                            disabled={loading || page >= meta.last_page}
                        >
                            Next →
                        </button>
                    </div>
                </div>
            )}

            {detailId !== null && (
                <AuditLogDetailModal
                    id={detailId}
                    onClose={() => setDetailId(null)}
                />
            )}
        </SuperAdminLayout>
    );
};

export default AuditLogPage;
